import type { Copy } from './en';
import { LOCALE_INFO } from './locale';

/**
 * Traditional Chinese (Taiwan) copy. Terms follow the app's own app_zh.arb
 * (打卡, 行程, 家鄉城市, 探索行程, 新增到我的旅程) so the site and app read the same.
 */

export const zhTW: Copy = {
  htmlLang: LOCALE_INFO['zh-TW'].htmlLang,
  phraseGap: '',
  ogLocale: 'zh_TW',
  languageName: '繁體中文',

  nav: {
    home: 'Travo 首頁',
    primary: '主選單',
    features: '功能',
    plan: '規劃旅程',
    about: '關於我們',
    download: '下載 App',
    openMenu: '開啟選單',
    closeMenu: '關閉選單',
    language: '語言',
    byLabs: 'Travo Labs 出品'
  },

  download: {
    appStore: '在 App Store 下載',
    playStore: '在 Google Play 下載',
    ios: 'iOS（App Store）',
    android: 'Android（Google Play）',
    free: '免費 · iOS 與 ANDROID'
  },

  seo: {
    ogImage: '/og-image-zh-tw.png',
    ogImageAlt: 'Travo — 日子過得飛快。Travo 替你記得。旅行足跡、護照印章與行程規劃，iOS 與 Android 免費下載。',
    home: {
      title: 'Travo｜自動旅行紀錄 App：足跡地圖、旅行護照與行程規劃',
      description:
        'Travo 是免費的旅行紀錄 App：一離開家就自動記錄旅程，畫出你的足跡地圖；每到一個新國家，旅行護照就多一枚印章；還能和朋友一起排旅遊行程。iOS 與 Android 免費下載。',
      keywords:
        '旅行紀錄 App, 旅遊紀錄, 足跡地圖, 旅行足跡, 去過的國家, 旅行護照, 行程規劃, 旅遊行程規劃, 旅行日記, 打卡, Travo'
    },
    about: {
      title: '關於 Travo｜每一天，都替你留下故事',
      description:
        '我們打造 Travo，是因為日子過得太快，最美好的片刻不該被遺忘。Travo 是自動記錄旅程的旅行 App，留住你挑選的片刻，也陪你規劃下一趟旅遊行程。',
      keywords: '關於 Travo, Travo Labs, 自動旅行紀錄, 旅行護照, 行程規劃, 旅行日記, 隱私'
    }
  },

  hero: {
    eyebrow: '旅程自動出現',
    titleLead: '日子過得飛快。',
    titleAccent: 'Travo 替你記得。',
    body: '一離開家，Travo 就替你開始記錄旅程——走過的每座城市、每個國家都留下足跡，護照上多一枚印章，下一趟的計畫也幫你收好。你只管出發。',
    photoAlt: '清晨山谷上空的熱氣球',
    phoneAlt: 'Travo 首頁地圖：台北的打卡照片釘選，以及旅行與 F1 地圖模式',
    stampLabel: '航空郵件'
  },

  band: ['地圖', '旅程', '打卡', '地點', '好友', '護照', '行程', '探索'],

  features: {
    title: '八站旅程，\n一個旅伴。',
    body: '記住走過的地方，珍藏值得回味的片刻，也規劃下一次出發。從這八站，開始你與 Travo 的旅程。',
    items: [
      {
        tag: '01 · 地圖',
        title: '把旅程連起來',
        body: '旅程、收藏地點、打卡與好友動態，都在同一張地圖上。輕點切換圖層，看你的旅行足跡逐漸展開。'
      },
      {
        tag: '02 · 旅程',
        title: '你只管出發，Travo 替你記得',
        body: '離開家，旅程就自動開始。Travo 記錄沿途走過的街區、城市與國家，不必手動輸入。'
      },
      {
        tag: '03 · 打卡',
        title: '留住這一刻',
        body: '每天最多打卡三次。加上一張照片，記下當時的心情與所在的地方。'
      },
      {
        tag: '04 · 地點',
        title: '收藏下一次想去的地方',
        body: '把心動的地點收進清單，留待下次出發。也能透過分享，將 Google Maps 收藏清單匯入 Travo。'
      },
      {
        tag: '05 · 好友',
        title: '分享彼此的旅程',
        body: '看看好友最近在哪裡打卡，掃描 QR Code，交換彼此的旅行印章卡。'
      },
      {
        tag: '06 · 護照',
        title: '一本裝滿回憶的護照',
        body: '每到訪一個國家，就收集一枚印章。記錄搭過的航班，看看自己飛了多遠。'
      },
      {
        tag: '07 · 行程',
        title: '一起規劃每一天',
        body: '把收藏地點、住宿與航班排進每日行程，邀請朋友一起編輯，為下一趟旅程做好準備。'
      },
      {
        tag: '08 · 探索',
        title: '尋找下一趟旅程的靈感',
        body: '探索其他旅人分享的行程，加入自己的旅程，再調整成你喜歡的樣子。'
      }
    ],
    extrasLabel: '還有這些',
    extras: [
      '主畫面小工具：護照地圖、即將旅程、行程',
      '可分享的每月回顧',
      'F1 外掛：賽程、賽果與積分排名，直接顯示在地圖上'
    ]
  },

  checkins: {
    eyebrow: '03 · 打卡',
    title: '為今天蓋章。\n每天最多三枚。',
    body: '日常的軌跡，Travo 會自己記下；打卡則是你親手挑選的片刻——附上照片，地圖就會記住你站過的地方。每天幾則，累積起來就是你的旅行日記。',
    stamps: ['一頓好料', '絕美景點', '和朋友出遊'],
    screenshotAlt: '2026 年 6 月的打卡日曆，附打卡照片與冰島旅程'
  },

  passport: {
    eyebrow: '06 · 護照',
    title: '每個國家，\n都有一枚印章。',
    body: '旅行時，護照會自己慢慢填滿——每年一頁印章，造訪的國家數和在外的天數都幫你算好。',
    points: [
      '每年一頁印章，自動統計國家數與旅行天數',
      '航班記在護照上，累計飛行公里數',
      '每月回顧整理照片與足跡，隨時可以分享',
      '主畫面上的「護照地圖」小工具'
    ],
    sample: '範例',
    thailand: '泰國',
    indonesia: '印尼',
    japanAlt: '範例印章：日本京都',
    taiwanAlt: '範例印章：台灣台北'
  },

  plan: {
    eyebrow: '07、08 · 行程與探索',
    title: '規劃每一天，\n或從別人的規劃開始。',
    body: '把收藏的地點排成每日行程，景點、住宿和航班一目了然。分享出去，朋友也能一起編輯；或到「探索行程」找一份別人排好的，新增到你的旅程後再慢慢改。',
    points: [
      '景點、住宿、航班，都在同一條每日時間軸上',
      '預訂清單，提醒你還有哪些要訂',
      '主畫面小工具，顯示「現在」與「接下來」',
      '探索行程：依國家與天數搜尋，一鍵新增到你的旅程'
    ],
    widget: {
      day: 'DAY 2/10 · 黃金圈',
      label: '小工具',
      nowLabel: '現在 · 09:30',
      now: '辛格韋德利國家公園',
      nextLabel: '接下來 · 12:30',
      next: '蓋錫爾間歇泉',
      later: '今天稍後：黃金瀑布',
      more: '還有 1 個'
    },
    discover: {
      kicker: '探索行程 · 冰島',
      length: '10 天',
      title: '冰島自駕環島10日遊',
      byline: '作者：Travo（@travo）· 999 人加入',
      cta: '新增到我的旅程'
    }
  },

  how: {
    eyebrow: '旅行路線',
    title: '設定一次，接著出發就好。',
    steps: [
      {
        tag: '家鄉城市',
        title: '告訴 Travo 你的家在哪',
        body: '這樣它才知道你什麼時候在旅行、什麼時候回到家。'
      },
      {
        tag: '出發',
        title: '授權一次位置權限',
        body: '省電的背景追蹤，隨時可以在設定中關閉。'
      },
      {
        tag: '旅途中',
        title: '離開家',
        body: '旅程自動開始，沿途的每座城市與國家都會被記下。'
      },
      {
        tag: '抵達',
        title: '蓋章、規劃、分享',
        body: '為精彩片刻打卡、蒐集護照印章，再和朋友一起規劃下一趟。'
      }
    ],
    note: '以前的旅行也想補上？Travo 可以從相簿偵測過往旅程。它只讀取照片的拍攝地點——照片不會被上傳或儲存。'
  },

  friends: {
    eyebrow: '05 · 好友',
    title: '一起旅行，\n即使分隔兩地。',
    body: '加朋友，就能在地圖上看到他們最近在哪裡打卡。用 QR code 交換專屬卡片，或直接傳個人頁面連結——掃描就能追蹤這趟旅程。',
    to: '收件人：每一位一起旅行過的人',
    from: '寄件人：app.travolabs.com/@travo',
    cta: '觀看示範影片',
    caption: '寄自你去過的每個地方',
    mapAlt: '世界地圖上擺著護照、相機與旅行紀念品'
  },

  cta: {
    eyebrow: '航空郵件 · 優先寄送',
    title: '你的護照，正在等你。',
    body: '免費下載 Travo。日常生活、週末小旅行，還有正在計畫的下一趟——全都在這裡。'
  },

  footer: {
    tagline: '記住每一段旅程，也規劃下一段。',
    product: '產品',
    community: '社群',
    legal: '法律條款',
    privacy: '隱私權政策',
    terms: '使用條款',
    rights: 'TRAVO LABS. 版權所有。'
  },

  about: {
    eyebrow: '關於 Travo Labs',
    titleLead: '日子匆匆，',
    titleAccent: 'Travo 替你記得。',
    intro: [
      '我們打造 Travo，是為了留住生活中值得珍藏的地方與片刻。從熟悉的街角到遠方的城市，Travo 默默記錄你的足跡，讓你專心感受旅程中的每一刻。',
      '出發後，旅程自動開始記錄。每到訪一個國家，旅行護照就多一枚印章。收藏的地點，也成為下一趟冒險的起點。',
      '隨時打開 Travo，回味走過的地方，看看好友的旅行近況，再規劃下一次出發。'
    ],
    photoAlt: '夜晚的城市天際線',
    caption: '來自旅途的問候',
    captionTag: 'NO. 00 · 關於',
    stampLabel: '航空郵件',
    missionEyebrow: '我們的使命',
    missionLead: '讓保存人生旅程這件事，變得',
    missionAccent: '毫不費力',
    missionTail: '又充滿意義。',
    salutation: '親愛的旅人：',
    letter: [
      '我們相信，最好的旅行 App，是你會忘記自己正在使用的那一個。Travo 會察覺你離開了家，替你開始記錄旅程——從自家街口到世界的另一端，每個街區、城市和國家都不漏掉。',
      '其餘的，就交給你選擇：為重要的片刻打幾次卡、讓護照慢慢蓋滿印章、和朋友一起排行程，或直接借用其他旅人的規劃。你專心旅行，紀錄交給 Travo。多年以後，每一段大大小小的冒險，都會留在它發生的地方。'
    ],
    signature: '— Travo',
    whyTitle: '為什麼選擇 Travo？',
    whyBody: '四個承諾，蓋章為憑。',
    reasons: [
      {
        title: '旅程自動出現',
        body: '離開家鄉城市，旅程就自動開始。每座城市、每個國家都會被記下，護照也會蒐集印章。'
      },
      {
        title: '由你挑選的片刻',
        body: '每天最多三則打卡，照片釘在你站過的地方，累積成值得一再翻閱的旅行日記。'
      },
      {
        title: '一起規劃',
        body: '收藏地點、排出朋友也能編輯的每日行程，或從其他旅人公開的規劃開始。'
      },
      {
        title: '隱私優先',
        body: '追蹤隨時可以關閉。偵測過往旅程時只讀取照片的拍攝地點，照片不會離開你的手機。'
      }
    ],
    ctaEyebrow: '你的下一枚郵戳',
    ctaTitle: '盡情去玩，\n我們幫你記下。',
    ctaBody: 'Travo 在 iOS 和 Android 上免費提供。授權一次，你的地圖就會自己慢慢填滿。',
    breadcrumbAbout: '關於我們'
  },

  schema: {
    appDescription:
      '免動手的旅行紀錄、旅行護照與行程規劃 App。離開家鄉城市，旅程就自動開始，記下每座城市與國家，每個新國家都會在護照上多一枚印章。每天最多三則打卡，和朋友一起排每日行程，或從「探索行程」的公開規劃開始。',
    featureList: [
      '離開家鄉城市就自動開始的旅程',
      '打卡：每天最多三則照片片刻',
      '旅行護照：每個國家一枚印章，並累計飛行里程',
      '每日行程：住宿、航班與多人共同編輯',
      '探索行程：瀏覽公開的旅行規劃並新增到你的旅程',
      '收藏地點清單，支援匯入 Google 地圖清單',
      '朋友地圖與 QR code 專屬卡片',
      '從照片拍攝地點偵測過往旅程',
      '主畫面小工具與每月回顧'
    ],
    videoName: 'Travo 實際操作 — 自動旅行紀錄示範',
    videoDescription: '看看 Travo 如何自動記錄你的日常路線與遠方冒險，並把去過的每個地方畫成一張互動地圖。'
  },

  notFound: {
    title: '找不到頁面｜Travo',
    eyebrow: '退回寄件人',
    heading: '查無此地址。',
    body: '你要找的頁面可能已經搬家了。回到首頁，從那裡重新出發吧。',
    cta: '回到首頁'
  },

  faqSection: {
    eyebrow: '常見問題',
    title: '你可能想問的。',
    body: 'Travo 能做什麼、要不要錢、誰看得到你的旅程——一次說清楚。'
  },

  faq: [
    {
      question: 'Travo 是什麼？',
      answer:
        'Travo 是 Travo Labs 開發的免費旅行紀錄 App，支援 iOS 與 Android。一離開家鄉城市就會自動開始記錄旅程，記下走過的每座城市與國家，每到一個新國家就在旅行護照上蓋一枚印章，還能和朋友一起一天一天規劃下一趟行程。'
    },
    {
      question: 'Travo 有中文版嗎？',
      answer:
        '有。Travo App 與這個網站都提供繁體中文，也支援英文、日文、韓文和泰文。'
    },
    {
      question: 'Travo 怎麼記錄我的旅程？',
      answer:
        'Travo 使用省電的背景定位。設定好家鄉城市後，一離開家旅程就會自動開始，沿途經過的每個街區、城市和國家都會被記下——不必手動輸入。'
    },
    {
      question: 'Travo 免費嗎？',
      answer: '是的，Travo 在 iOS 與 Android 上都可以免費下載與使用。'
    },
    {
      question: 'Travo 會很耗電嗎？',
      answer: '不會。背景追蹤經過省電最佳化，也可以隨時在設定中關閉。'
    },
    {
      question: '什麼是打卡？',
      answer:
        '每天最多三則打卡，留給你想記住的片刻。附上照片，地圖就會記住你站過的地方，累積起來就是你的旅行日記。'
    },
    {
      question: 'Travo 的護照是什麼？',
      answer:
        '每去一個國家，護照就多一枚印章。每年都有自己的一頁，自動統計國家數與在外天數；記錄的航班也會累計飛行公里數。'
    },
    {
      question: '可以用 Travo 規劃旅程嗎？',
      answer:
        '可以。把收藏的地點排成每日行程，加入住宿與航班；分享給朋友一起編輯，或從「探索行程」把別人公開的規劃新增到你的旅程。'
    },
    {
      question: '安裝前的旅行也能補進來嗎？',
      answer: '可以。「偵測過往旅程」會讀取照片的拍攝地點來找出以前的旅行，照片不會被上傳或儲存。'
    },
    {
      question: '誰看得到我的旅程？',
      answer: '由你決定。你的位置資料是私密的，你可以選擇哪些朋友能追蹤你，也能隨時在設定中關閉追蹤。'
    }
  ]
};
